import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { yoyoData } from './yoyoData';
import { useCart } from './CartContext';
import { trackAnalyticsEvent } from '../utils/analytics';
import './YoYoPage.css';
import { FaArrowLeft } from 'react-icons/fa';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import getApiBase from '../utils/apiBase';
import Seo from '../components/Seo';
import { useI18n } from '../i18n/I18nContext';
import { getProductFaqs } from './productFaqs';
import { getProductTags } from './productFilters';
import { findProductByRouteParam, getProductPath } from '../utils/productSlug';

const API_BASE = getApiBase();

const YoYoPage = () => {
  const { productSlug } = useParams();
  const [yoyo, setYoyo] = useState(null);
  const [canonicalProductPath, setCanonicalProductPath] = useState("");
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { dispatch } = useCart();
  const navigate = useNavigate();
  const { t, formatNumber } = useI18n();

  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState("");

  useEffect(() => {
    let isMounted = true;
    const loadProduct = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/products/${encodeURIComponent(productSlug || "")}`);
        if (!response.ok) {
          throw new Error("Failed to fetch");
        }
        const data = await response.json();
        const found = data.product || null;
        const nextRelatedProducts = Array.isArray(data.relatedProducts) ? data.relatedProducts : [];
        const canonicalPath = data.canonicalPath || found?.path || "";
        if (isMounted) {
          setYoyo(found || null);
          setCanonicalProductPath(canonicalPath);
          setRelatedProducts(nextRelatedProducts);
          setLoading(false);
        }
        if (found && canonicalPath) {
          if (productSlug && canonicalPath !== `/store/${productSlug}`) {
            navigate(canonicalPath, { replace: true });
          }
        }
      } catch (error) {
        const found = findProductByRouteParam(yoyoData, productSlug);
        if (isMounted) {
          setYoyo(found || null);
          setCanonicalProductPath(found ? getProductPath(found, yoyoData) : "");
          setRelatedProducts(
            yoyoData.filter((item) => item.id !== found?.id && item.id !== 7).slice(0, 3)
          );
          setLoading(false);
        }
        if (found) {
          const canonicalPath = getProductPath(found, yoyoData);
          if (productSlug && canonicalPath !== `/store/${productSlug}`) {
            navigate(canonicalPath, { replace: true });
          }
        }
      }
    };
    loadProduct();
    return () => {
      isMounted = false;
    };
  }, [navigate, productSlug]);

  const colors = useMemo(() => (
    yoyo?.colors?.length
      ? yoyo.colors
      : yoyo
        ? [{ name: "Default", imageUrl: yoyo.imageUrl, price: yoyo.price }]
        : []
  ), [yoyo]);

  useEffect(() => {
    if (colors.length) {
      setSelectedColor(colors[0].name);
    }
  }, [colors]);

  // Get the image and price for the currently selected color
  const selectedColorDetails = colors.find(color => color.name === selectedColor);
  const currentImage = selectedColorDetails?.imageUrl;
  const currentPrice = selectedColorDetails?.price || yoyo?.price || 0; // Fallback to default price if not specified
  const formatPrice = (value) => formatNumber(value, { style: 'currency', currency: 'USD' });
  const faqs = getProductFaqs(yoyo, t, formatPrice);
  const displayedColor = selectedColor === 'Default' ? t('product.defaultColor') : selectedColor;


  const addItemToCart = () => {
    if (!yoyo) return;
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        ...yoyo,
        quantity: parseInt(quantity),
        selectedColor,
        imageUrl: currentImage, // Save the selected color image in the cart
        price: currentPrice, // Save the price based on the selected color
      },
    });
  };

  if (loading) {
    return (
      <div className="yoyo-page">
        <Seo
          title={t('product.loadingTitle')}
          description={t('product.loadingDescription')}
          path="/store"
          noindex
        />
        <p>{t('product.loading')}</p>
      </div>
    );
  }

  if (!yoyo) {
    return (
      <div className="yoyo-page">
        <Seo
          title={t('product.notFoundTitle')}
          description={t('product.notFoundDescription')}
          path={`/store/${productSlug || ''}`}
          noindex
        />
        <button onClick={() => navigate(-1)} className="back-button">
          <FaArrowLeft /> {t('product.back')}
        </button>
        <p>{t('product.notFound')}</p>
      </div>
    );
  }

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const productPath = canonicalProductPath || yoyo.path || getProductPath(yoyo, [yoyo, ...relatedProducts]);
  const imageSource = resolveImageUrl(currentImage || yoyo.imageUrl);
  const imageUrl = imageSource.startsWith('http')
    ? imageSource
    : `${siteUrl}${imageSource.startsWith('/') ? imageSource : `/${imageSource}`}`;
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: yoyo.name,
    description: yoyo.description,
    image: [imageUrl],
    sku: String(yoyo.id),
    brand: {
      '@type': 'Brand',
      name: getProductTags(yoyo).brand?.[0] || 'YCH-YOYO',
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: Number(currentPrice).toFixed(2),
      availability: 'https://schema.org/InStock',
      url: `${siteUrl}${productPath}`,
    },
  };
  const faqSchema = faqs.length
    ? {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    }
    : null;
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('nav.home'),
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('nav.store'),
        item: `${siteUrl}/store`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: yoyo.name,
        item: `${siteUrl}${productPath}`,
      },
    ],
  };

  return (
    <div className="yoyo-page">
      <Seo
        title={yoyo.name}
        description={yoyo.description}
        path={productPath}
        image={currentImage || yoyo.imageUrl}
        type="product"
        structuredData={[productSchema, faqSchema, breadcrumbSchema]}
      />
      <nav className="breadcrumb-nav" aria-label={t('product.breadcrumb')}>
        <Link to="/">{t('nav.home')}</Link> / <Link to="/store">{t('nav.store')}</Link> / <span>{yoyo.name}</span>
      </nav>
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> {t('product.back')}
      </button>
      <div className="yoyo-content">
        {/* Main Yo-Yo Image */}
        <div className="main-image">
          <img
            src={resolveImageUrl(currentImage)}
            alt={t('product.mainImageAlt', { product: yoyo.name, color: displayedColor })}
          />
        </div>
        {/* Yo-Yo Details */}
        <div className="yoyo-details">
          <h1>{yoyo.name}</h1>
          <p>{t('product.color', { color: displayedColor })}</p>
          <p>{t('common.price', { price: formatPrice(Number(currentPrice)) })}</p>
          {/* Color Selection */}
          <div className="color-selection">
            <div className="color-options">
              {colors.map(color => (
                <img
                  key={color.name}
                  src={resolveImageUrl(color.imageUrl)}
                  alt={color.name}
                  className={`color-thumbnail ${color.name === selectedColor ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedColor(color.name);
                    trackAnalyticsEvent('click', { target: 'product.color_select', productId: yoyo.id });
                  }}
                />
              ))}
            </div>
          </div>

          {/* Quantity Selection */}
          <div className="quantity-selection">
            <label htmlFor="quantity">{t('product.quantity')} </label>
            <input
              type="number"
              id="quantity"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              min="1"
            />
          </div>
          <button data-analytics-target="product.add_to_cart" className="add-to-cart-btn" onClick={addItemToCart}>{t('product.addToCart')}</button>


          {/* Includes*/}
          <h3>{t('product.includes')}</h3>
          <p>{yoyo.include}</p>
          {/* Description */}
          <h3>{t('product.description')}</h3>
          <p>{yoyo.description}</p>
          {/* Conditionally display the video */}
          {yoyo.videoUrl && (
            <div className="yoyo-video">
              <iframe
                title={t('product.demoVideo', { name: yoyo.name })}
                src={yoyo.videoUrl}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          )}

          {faqs.length > 0 && (
            <section className="product-faqs" aria-label={t('product.faqAria', { name: yoyo.name })}>
              <h3>{t('product.faq')}</h3>
              {faqs.map((item) => (
                <div key={item.question} className="product-faq-item">
                  <h4>{item.question}</h4>
                  <p>{item.answer}</p>
                </div>
              ))}
            </section>
          )}

          {relatedProducts.length > 0 && (
            <section className="related-products" aria-label={t('product.relatedAria')}>
              <h3>{t('product.related')}</h3>
              <ul>
                {relatedProducts.map((product) => (
                  <li key={product.id}>
                    <Link to={product.path || getProductPath(product, [yoyo, ...relatedProducts])}>
                      {product.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default YoYoPage;
